import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import CrmDetailHeader from '../../shared/components/CrmDetailHeader';
import CrmTabsRow from '../../shared/components/CrmTabsRow';
import CrmNotesCard, { deriveNoteTitle } from '../../shared/components/CrmNotesCard';
import CrmBreadcrumb from '../../shared/components/CrmBreadcrumb';
import CrmPageLayout from '../../shared/components/CrmPageLayout';
import TaskFormModal from '../../Tasks/components/TaskFormModal';
import { customFieldDisplayRows, renderCustomFieldRowValue } from '../../shared/utils/customFieldHelpers';
import { resolveToken } from '../../../shared/marketing/themeTokens';

const axios = require('axios').default;

// ponytail: categorical task-status swatch palette (used as text/dot color) — data, not theme chrome; intentionally not tokenized
const STATUS_CONFIG = {
  open: { label: 'Open', color: '#717680' },
  in_progress: { label: 'In Progress', color: '#A2DC47' },
  done: { label: 'Done', color: '#12B76A' },
  archived: { label: 'Archived', color: '#9CA3AF' },
};

function TasksDetailPage({
  team,
  task,
  csrfToken,
  currentUser,
  statuses = [],
  customFieldDefinitions = [],
}) {
  if (!team || !task) return null;

  const [assignees, setAssignees] = useState([]);
  const [events, setEvents] = useState([]);
  const [currentTask, setCurrentTask] = useState(task);
  const [activeTab, setActiveTab] = useState('overview');
  const [formModalOpen, setFormModalOpen] = useState(false);

  const customFieldRows = customFieldDisplayRows(customFieldDefinitions, currentTask.custom_field_values);

  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [noteErrors, setNoteErrors] = useState([]);
  const [editingNoteId, setEditingNoteId] = useState(null);

  useEffect(() => {
    if (!team || !team.id || !csrfToken) return;

    const fetchDropdownData = async () => {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      try {
        const [assigneesRes, eventsRes] = await Promise.all([
          axios.get(`/teams/${team.id}/crm/tasks/assignees`),
          axios.get(`/teams/${team.id}/crm/tasks/available_events`),
        ]);

        setAssignees(assigneesRes.data.assignees || []);
        setEvents(eventsRes.data.events || []);
      } catch (e) {
        toast.error('Failed to load dropdown data.');
      }
    };

    fetchDropdownData();
  }, [team && team.id, csrfToken]);

  const refreshNotes = async () => {
    if (!team || !currentTask.id) return;
    try {
      setNotesLoading(true);
      setNotesError(null);
      const response = await axios.get(`/teams/${team.id}/crm/notes.json`, {
        params: { notable_type: 'Task', notable_id: currentTask.id },
      });
      setNotes(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      setNotesError('Failed to load notes.');
    } finally {
      setNotesLoading(false);
    }
  };

  useEffect(() => {
    refreshNotes();
  }, [team, currentTask.id]);

  const handleCreateNote = async (body) => {
    if (!team || !team.id || !currentTask || !currentTask.id || !csrfToken) {
      throw new Error('Missing context to create note');
    }
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    await axios.post(`/teams/${team.id}/crm/notes.json`, {
      note: {
        title: deriveNoteTitle(body),
        body,
        notable_type: 'Task',
        notable_id: currentTask.id,
      },
    });
    await refreshNotes();
  };

  const canSubmitNoteForm = noteTitle && noteTitle.trim().length > 0;

  const handleSaveNote = async () => {
    if (!canSubmitNoteForm) return;
    try {
      setNoteSubmitting(true);
      setNoteErrors([]);
      if (csrfToken) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      }
      const payload = {
        note: {
          title: noteTitle,
          body: noteBody,
          notable_type: 'Task',
          notable_id: currentTask.id,
        },
      };
      if (editingNoteId) {
        await axios.patch(`/teams/${team.id}/crm/notes/${editingNoteId}.json`, payload);
      } else {
        await axios.post(`/teams/${team.id}/crm/notes.json`, payload);
      }
      setShowNoteModal(false);
      setNoteTitle('');
      setNoteBody('');
      setEditingNoteId(null);
      refreshNotes();
    } catch (e) {
      const messages = Array.isArray(e.response && e.response.data)
        ? e.response.data
        : ['Failed to save note'];
      setNoteErrors(messages);
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setNoteTitle(note.title || '');
    setNoteBody(note.body || '');
    setNoteErrors([]);
    setShowNoteModal(true);
  };

  const handleDeleteNote = async (note) => {
    if (!team || !team.id || !csrfToken || !note || !note.id) return;
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(`/teams/${team.id}/crm/notes/${note.id}.json`);
      refreshNotes();
    } catch (e) {
      toast.error('Failed to delete note.');
    }
  };

  const title = currentTask.title || '(No title)';
  const subtitle = currentTask.assignee_name || 'Unassigned';

  useEffect(() => {
    setCurrentTask(task);
  }, [task]);

  function formatDueDate(dateStr) {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    if (team.time_zone) options.timeZone = team.time_zone;
    return date.toLocaleDateString('en-US', options);
  }

  function formatCreatedAt(dateStr) {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const options = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
    if (team.time_zone) options.timeZone = team.time_zone;
    return date.toLocaleDateString('en-US', options);
  }

  const startEditing = () => setFormModalOpen(true);

  const handleTaskSaved = (saved) => {
    setCurrentTask((prev) => ({ ...prev, ...saved }));
  };

  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, color: '#717680' };

    return (
      <div
        style={{
          background: 'var(--sl-bg-primary)',
          border: '1px solid var(--sl-border-primary)',
          borderRadius: '6px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 8px',
        }}
      >
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: config.color,
            flexShrink: 0,
          }}
        />
        <span className="figma-text secondary xs semibold">{config.label}</span>
      </div>
    );
  };

  return (
    <CrmPageLayout team={team} activeSidebarKey="tasks">
          <div className="card mb-3 cdp-main-card">
            <div className="card-body">
              <CrmBreadcrumb
                href={`/teams/${team.id}/crm/tasks`}
                icon={
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path
                      d="M6 10L8.5 12.5L14 7M6.5 17.5H13.5C14.9001 17.5 15.6002 17.5 16.135 17.2275C16.6054 16.9878 16.9878 16.6054 17.2275 16.135C17.5 15.6002 17.5 14.9001 17.5 13.5V6.5C17.5 5.09987 17.5 4.3998 17.2275 3.86502C16.9878 3.39462 16.6054 3.01217 16.135 2.77248C15.6002 2.5 14.9001 2.5 13.5 2.5H6.5C5.09987 2.5 4.3998 2.5 3.86502 2.77248C3.39462 3.01217 3.01217 3.39462 2.77248 3.86502C2.5 4.3998 2.5 5.09987 2.5 6.5V13.5C2.5 14.9001 2.5 15.6002 2.77248 16.135C3.01217 16.6054 3.39462 16.9878 3.86502 17.2275C4.3998 17.5 5.09987 17.5 6.5 17.5Z"
                      stroke={resolveToken('--sl-text-quaternary', '#717680')}
                      strokeWidth="1.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                ariaLabel="Back to tasks"
                label="Task Details"
              />

              <CrmDetailHeader
                avatarProps={{ name: title }}
                title={title}
                subtitle={subtitle}
                primaryButton={{
                  label: 'Edit task',
                  iconClass: 'fal fa-check-square',
                  onClick: startEditing,
                }}
              />

              <CrmTabsRow
                activeKey={activeTab}
                onChange={setActiveTab}
                tabs={[
                  { key: 'overview', label: 'Overview' },
                ]}
              />

              {activeTab === 'overview' && (
                <>
                  <div className="row g-3 mt-3" style={{ rowGap: 24 }}>
                      <div className="col-md-12">
                        <div className="cdp-field-label">Title</div>
                        <div>{currentTask.title || '—'}</div>
                      </div>

                      <div className="col-md-12">
                        <div className="cdp-field-label">Status</div>
                        <div>{renderStatusBadge(currentTask.status)}</div>
                      </div>

                      <div className="col-md-12">
                        <div className="cdp-field-label">Description</div>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{currentTask.description || '—'}</div>
                      </div>

                      <div className="col-md-3">
                        <div className="cdp-field-label">Due Date</div>
                        <div>{formatDueDate(currentTask.due_at)}</div>
                      </div>

                      <div className="col-md-3">
                        <div className="cdp-field-label">Assigned To</div>
                        <div>{currentTask.assignee_name || 'Unassigned'}</div>
                      </div>

                      <div className="col-md-3">
                        <div className="cdp-field-label">Related Event</div>
                        <div>{currentTask.calendar_event_title || '—'}</div>
                      </div>

                      <div className="col-md-3">
                        <div className="cdp-field-label">Created</div>
                        <div>{formatCreatedAt(currentTask.created_at)}</div>
                      </div>
                    </div>

                  {customFieldRows.length > 0 && (
                    <div className="row mt-3">
                      {customFieldRows.map((row) => (
                        <div className="col-md-4 mb-3" key={row.definition.id}>
                          <h6 className="cdp-field-label">{row.definition.name}</h6>
                          <div className="cdp-field-value crm-custom-field-value">{renderCustomFieldRowValue(row, team?.id)}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ background: 'var(--sl-bg-primary-alt)', margin: '16px -24px -24px', padding: '16px 24px 24px', borderRadius: '0 0 12px 12px' }}>
                    <div className="row">
                      <div className="col-lg-6">
                        <CrmNotesCard
                          notes={notes}
                          loading={notesLoading}
                          error={notesError}
                          onCreateNote={handleCreateNote}
                          onEditNote={handleEditNote}
                          onDeleteNote={handleDeleteNote}
                          canManageNotes
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

      {showNoteModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingNoteId ? 'Edit Note' : 'Add Note'}</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowNoteModal(false)}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {noteErrors.length > 0 && (
                  <div className="alert alert-danger">
                    <ul className="mb-0">
                      {noteErrors.map((msg) => (
                        <li key={msg}>{msg}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={noteTitle}
                    onChange={(e) => setNoteTitle(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group mt-2">
                  <label>Body</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowNoteModal(false)}
                  disabled={noteSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveNote}
                  disabled={!canSubmitNoteForm || noteSubmitting}
                >
                  {noteSubmitting ? 'Saving…' : editingNoteId ? 'Update Note' : 'Add Note'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <TaskFormModal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        task={currentTask}
        team={team}
        csrfToken={csrfToken}
        statuses={statuses}
        assignees={assignees}
        events={events}
        customFieldDefinitions={customFieldDefinitions}
        onSaved={handleTaskSaved}
      />
    </CrmPageLayout>
  );
}

export default TasksDetailPage;
